<template>
  <div class="home-container">
    <!-- 头部区域 -->
    <header class="header">
      <div class="container">
        <h1 class="main-title">{{ siteInfo.title }}</h1>
        <p class="subtitle">{{ siteInfo.subtitle }}</p>
        <div class="slogan-box">
          <p class="slogan">{{ siteInfo.slogan }}</p>
        </div>
        <div>
          <span v-for="badge in specialtyBadges" 
                :key="badge" 
                class="specialty-badge">
            {{ badge }}
          </span>
        </div>
      </div>
    </header>

    <!-- 平台介绍 -->
    <section class="mission-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <h2 class="mission-title">{{ mission.title }}</h2>
            <p v-for="(text, index) in mission.texts" 
               :key="index" 
               class="mission-text">
              {{ text }}
            </p>
            <blockquote class="teacher-quote">{{ mission.quote }}</blockquote>
            <a :href="mission.buttonLink" class="btn btn-teacher">{{ mission.buttonText }}</a>
          </div>
          <div class="col-lg-6 mt-5 mt-lg-0">
            <img src="@/assets/img/main.png" alt="教学平台界面" class="img-fluid rounded shadow">
          </div>
        </div>
      </div>
    </section>

    <!-- 专业方向 -->
    <section class="py-5">
      <div class="container">
        <h2 class="text-center mb-5" style="color: var(--primary-color);">专业教学方向</h2>
        <div class="row">
          <div v-for="feature in features" 
               :key="feature.title" 
               class="col-md-6 col-lg-3 mb-4">
            <div class="feature-card p-4 text-center">
              <div class="feature-icon">
                <i :class="feature.icon"></i>
              </div>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p>
                <span class="badge" :class="feature.badgeClass">{{ feature.badge }}</span>
                {{ feature.description }}
              </p>
              <div class="feature-footer">
                <small>{{ feature.footer }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 教学特色 -->
    <section class="pedagogy-section">
      <div class="container">
        <h2 class="text-center mb-5" style="color: var(--primary-color);">{{ pedagogy.title }}</h2>
        <div class="row">
          <div v-for="(column, colIndex) in pedagogy.columns" 
               :key="colIndex" 
               class="col-lg-6">
            <div v-for="item in column" 
                 :key="item.title" 
                 class="pedagogy-item">
              <div class="pedagogy-icon">
                <i :class="item.icon"></i>
              </div>
              <div class="pedagogy-content">
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 教育理念 -->
    <section class="quote-section">
      <div class="container text-center">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <p class="quote-text">{{ educationQuote.text }}</p>
            <p class="quote-author">{{ educationQuote.author }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 页脚 -->
    <footer class="footer">
      <div class="container">
        <div class="row">
          <div v-for="(section, index) in footerSections" 
               :key="index" 
               :class="section.class">
            <h4 class="footer-title">{{ section.title }}</h4>
            <template v-if="section.type === 'text'">
              <p>{{ section.content }}</p>
            </template>
            <template v-else-if="section.type === 'links'">
              <a v-for="link in section.links" 
                 :key="link.url" 
                 :href="link.url" 
                 class="footer-link">
                {{ link.text }}
              </a>
            </template>
            <template v-else-if="section.type === 'contact'">
              <p v-for="(contact, cIndex) in section.contacts" 
                 :key="cIndex">
                <i :class="contact.icon"></i> {{ contact.text }}
              </p>
            </template>
          </div>
        </div>
        <div class="copyright">
          <p>
            <a href="https://beian.miit.gov.cn/" target="_blank">津ICP备2023006063号</a> 
            © 2023-{{ currentYear }} e4glet.cn
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      currentYear: new Date().getFullYear(),
      siteInfo: {
        title: 'E4Glet.cn',
        subtitle: '高校计算机专业教学辅助平台',
        slogan: '在线教学，构建高校计算机专业教学新模式'
      },
      specialtyBadges: ['人工智能', '软件工程', '大数据', '网络空间安全'],
      mission: {
        title: '平台定位',
        texts: [
          'E4GLET.CN是由高校计算机专业教师自发创建的教学辅助平台，旨在通过创新的在线教学方式，补充和提升传统课堂教学效果，构建计算机专业教学新模式。',
          '坚持社会主义办学方向，在教育强国的战略背景下。突出青年教师积极主动参与到高层次人才、高价值转化、高效能资源、高水平教学、高效率管理的"五高"战略中去。'
        ],
        quote: '"每一位教师要提高站位。做教育强国的践行者，把个人理想融入党和国家的发展伟业，以立德树人为根本，树立高尚的师德师风，培育良好的校风学风。"',
        buttonText: '学生入口',
        buttonLink: 'https://www.e4glet.cn/study/#/'
      },
      features: [
        {
          icon: 'fas fa-robot',
          title: '人工智能',
          badge: 'DeepSeek集成',
          badgeClass: 'bg-primary',
          description: '基于大语言模型的智能教学系统，提供机器学习、计算机视觉的代码库与实时答疑支持',
          footer: '含目标检测/YOLO实战案例'
        },
        {
          icon: 'fas fa-code',
          title: '软件工程',
          badge: 'Gitee托管',
          badgeClass: 'bg-info',
          description: '通过版本控制管理的项目资源库，提供学生丰富的应用开发实践指南',
          footer: '含SpringBoot、Vue、NodeJS核心内容'
        },
        {
          icon: 'fas fa-database',
          title: '大数据',
          badge: '代码案例',
          badgeClass: 'bg-warning',
          description: '提供详细环境搭建、训练代码，结合AI助手完成数据清洗、特征工程等核心学习',
          footer: '含Python实践案例'
        },
        {
          icon: 'fas fa-user-shield',
          title: '网络安全',
          badge: '攻防推演',
          badgeClass: 'bg-danger',
          description: '基于CTF赛题的防御体系教学，涵盖密码学、Web安全等模块的漏洞复现与分析',
          footer: '提供靶场环境配置指南'
        }
      ],
      pedagogy: {
        title: '教学辅助特色',
        columns: [
          [
            {
              icon: 'fas fa-book-open',
              title: '教学资源建设',
              description: '提供系统的课程资料库，包含完整的机器学习与目标识别课程讲义、代码实例及配套案例分析。所有教学材料均以文档形式呈现，便于学生分步骤研读与实践。'
            },
            {
              icon: 'fas fa-robot',
              title: '智能教学辅助',
              description: '创新性的集成DeepSeek大语言模型作为教学助手。该AI系统可实时响应学生关于计算机理论、编程实践及课程内容的技术咨询，提供7×24小时的专业答疑支持。'
            }
          ],
          [
            {
              icon: 'fas fa-code-branch',
              title: '开源资源共享',
              description: '通过Gitee平台共享课程代码仓库，系统化托管教学示例源码、项目模板及配套文档。支持版本控制管理，方便学生克隆学习、提交Issue讨论及参与协作开发。'
            },
            {
              icon: 'fas fa-project-diagram',
              title: '课程内容体系',
              description: '专注人工智能前沿领域教学，课程内容深度覆盖机器学习核心算法、计算机视觉中的目标检测技术，通过模块化知识单元与渐进式案例设计确保教学效果。'
            }
          ]
        ]
      },
      educationQuote: {
        text: '聚焦国家战略与行业需求，明确科研方向，规划学术路径。以战略思维布局原创性研究，争做科技"领跑者"；以系统思维提升成果转化效率。把立德树人作为根本任务，将思想政治教育贯穿教学全过程，培养既有扎实专业基础又有社会责任感的计算机专业人才。',
        author: '— 教育工作者'
      },
      footerSections: [
        {
          title: '关于E4Glet',
          type: 'text',
          class: 'col-md-4 mb-4',
          content: '突出"国家-学校-个人"三位一体的发展逻辑，强调使命引领与行动路径的结合，争做教育强国的先行者。'
        },
        {
          title: '快速链接',
          type: 'links',
          class: 'col-md-4 mb-4',
          links: [
            { url: 'https://www.e4glet.cn/study/#/', text: '学生入口' },
            { url: 'https://gitee.com/e4glet', text: '共享资源' },
            { url: '#', text: '帮助中心' }
          ]
        },
        {
          title: '联系我们',
          type: 'contact',
          class: 'col-md-4',
          contacts: [
            { icon: 'fas fa-envelope me-2', text: '317834473@qq.com' },
            { icon: 'fas fa-university me-2', text: '高校计算机专业教学辅助平台' }
          ]
        }
      ]
    }
  }
}
</script>

<style scoped>
/* 全局变量定义 */
:root {
  --primary-color: #d32f2f;
  --secondary-color: #1976d2;
  --dark-color: #212121;
  --light-color: #f5f5f5;
  --accent-color: #ff8f00;
}

body {
  font-family: 'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
  color: var(--dark-color);
  line-height: 1.6;
  background-color: white;
}

.header {
  background: url('@/assets/img/bg-header.jpg') no-repeat center center;
  background-size: cover;
  color: white;
  padding: 3rem 0;
  position: relative;
  overflow: hidden;
  text-align: center;
}

.header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1;
}

.header .container {
  position: relative;
  z-index: 2;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.subtitle {
  font-size: 1.2rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
}

.slogan-box {
  background-color: rgba(255, 255, 255, 0.2);
  border-left: 4px solid white;
  padding: 1rem;
  margin: 2rem auto;
  max-width: 800px;
}

.slogan {
  font-size: 1.1rem;
  margin: 0;
}

.feature-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  border-top: 4px solid var(--primary-color);
  overflow: hidden;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.feature-icon {
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.feature-title {
  font-weight: 700;
  color: var(--dark-color);
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.mission-section {
  background-color: var(--light-color);
  padding: 4rem 0;
}

.mission-title {
  color: var(--primary-color);
  font-weight: 700;
  margin-bottom: 2rem;
  position: relative;
  display: inline-block;
}

.mission-title::after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 50%;
  height: 3px;
  background-color: var(--accent-color);
}

.mission-text {
  font-size: 1.1rem;
  line-height: 1.8;
}

.teacher-quote {
  font-style: italic;
  border-left: 3px solid var(--secondary-color);
  padding-left: 1.5rem;
  margin: 2rem 0;
}

.specialty-badge {
  display: inline-block;
  background-color: var(--secondary-color);
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  margin: 0.25rem;
  font-weight: 500;
}

.btn-teacher {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 0.6rem 1.8rem;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.3s ease;
  text-decoration: none; /* 添加这行确保链接无下划线 */
  display: inline-block; /* 添加这行确保按钮正确显示 */
}

.btn-teacher:hover {
  background-color: #b71c1c;
  transform: translateY(-2px);
  color: white;
  text-decoration: none; /* 确保悬停时也无下划线 */
}

.pedagogy-section {
  background-color: white;
}

.pedagogy-item {
  display: flex;
  margin-bottom: 2rem;
}

.pedagogy-icon {
  font-size: 1.8rem;
  color: var(--primary-color);
  margin-right: 1.5rem;
  flex-shrink: 0;
}

.pedagogy-content h3 {
  color: var(--dark-color);
  margin-bottom: 0.5rem;
}

.quote-section {
  background-color: var(--light-color);
  padding: 3rem 0;
}

.quote-text {
  font-size: 1.2rem;
  line-height: 1.8;
  position: relative;
  padding: 0 2rem;
}

.quote-text::before {
  content: "\201C"; /* 使用 Unicode 转义序列替代直接的引号字符 */
  font-size: 4rem;
  color: var(--primary-color);
  opacity: 0.2;
  position: absolute;
  top: -1.5rem;
  left: -0.5rem;
}

.quote-author {
  font-weight: 600;
  color: var(--primary-color);
  margin-top: 1rem;
  font-size: 1rem;
}

.footer {
  background-color: var(--dark-color);
  color: white;
  padding: 3rem 0 1.5rem;
}

.footer-title {
  font-weight: 700;
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
}

.footer-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
  display: block;
  margin-bottom: 0.5rem;
}

.footer-link:hover {
  color: var(--accent-color);
  text-decoration: none;
}

.copyright {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1.5rem;
  margin-top: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
}

.copyright a {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.3s ease;
}

.copyright a:hover {
  color: var(--accent-color);
}

@media (max-width: 768px) {
  .header {
    padding: 2rem 0;
    background-position: center center;
  }
}

@media (max-width: 576px) {
  .header {
    padding: 1.5rem 0;
  }
  
  .specialty-badge {
    font-size: 0.8rem;
    padding: 0.3rem 0.8rem;
  }
}

/* 提高按钮样式优先级 */
.mission-section .btn-teacher {
  background-color: var(--primary-color) !important;
  color: white !important;
  border: none;
  padding: 0.6rem 1.8rem;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.mission-section .btn-teacher:hover {
  background-color: #b71c1c !important;
  transform: translateY(-2px);
  color: white !important;
  text-decoration: none;
}
</style>
